import {
  CalendarOutlined,
  ContactsOutlined,
  CreditCardOutlined,
  FolderOpenOutlined,
  InfoCircleOutlined,
  LineChartOutlined,
  MessageOutlined,
  PushpinOutlined,
  RightOutlined,
  ShoppingOutlined,
} from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { Layout, Menu, Tooltip, message } from 'antd';
import type { ItemType } from 'antd/es/menu/hooks/useItems';
import React, { useContext, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';

import { ExtranetLogo } from 'app/components/commons/icons/ExtranetLogo';
import { HotelContext } from 'app/context/HotelContext/HotelContext';
import { useExtranetHotelsList } from 'app/hooks/data/core/extranet/hotel/useHotelsList';
import { useHotelManager } from 'app/hooks/data/extranet/useHotelManager';
import { hotelPath } from 'app/hooks/data/hotels/useHotel';
import { useAppSelector } from 'app/redux/hooks';
import { copyToClipboard } from 'app/utils/clipboard';
import { differenceInCalendarDays } from 'app/utils/dayjs';
import { getNewExtranetUrl } from 'app/utils/extranet';
import { useSearchParams } from 'app/utils/searchParams';
import { amplitudeTrack } from 'app/utils/track/amplitude';

import 'styles/layout/side-menu.scss';

import { PictureComponent } from '../commons/PictureComponent/PictureComponent';
import { Tag } from '../commons/Tag/Tag';
import { numberToStars } from '../pages/Bookings/details/NumberToStars';

import './SideMenuHotel.scss';
import { SearchHotelModal } from './_components/SearchHotelModal';

const { Sider } = Layout;

type MenuSection = {
  title: string;
  link: string;
  icon: React.ReactElement;
  absolute?: boolean;
  tag?: React.ReactElement;
  amplitudeEvent?: {
    name: string;
    content: object;
  };
};

type MenuItems = Record<string, MenuSection>;

export const SideMenuHotel = () => {
  const { data: hotelsList } = useExtranetHotelsList();
  const { hotelId, hotelInfo, setHotelId } = useContext(HotelContext);
  const queryClient = useQueryClient();

  const HIDE_NEW_TAG_AFTER_X_DAYS = 30;

  const { setSearchParams } = useSearchParams();

  const { data: manager } = useHotelManager(hotelInfo?.id);

  const { pathname } = useAppSelector((s) => s.router.location);
  const menuItems: MenuItems = {
    extranetInventory: {
      title: 'Inventory',
      link: `/extranet/inventory?hotelId=${hotelId}`,
      icon: <CalendarOutlined />,
    },
    extranetDashboard: {
      title: 'Dashboard',
      link: `/extranet/dashboard?hotelId=${hotelId}`,
      icon: <LineChartOutlined />,
      tag:
        differenceInCalendarDays(new Date(), new Date(2024, 3, 29)) <
        HIDE_NEW_TAG_AFTER_X_DAYS ? (
          <Tag text={'New'} />
        ) : undefined,
    },
    extranetBookings: {
      title: 'Bookings',
      link: `/extranet/bookings?hotelId=${hotelId}`,
      icon: <ShoppingOutlined />,
    },
    extranetBills: {
      title: 'Bills',
      link: `/extranet/bills?hotelId=${hotelId}`,
      icon: <FolderOpenOutlined />,
    },
    extranetReviews: {
      title: 'Reviews',
      link: `/extranet/reviews?hotelId=${hotelId}`,
      icon: <MessageOutlined />,
    },
    packageDetail: {
      title: 'Packages',
      link: `/extranet/packages?hotelId=${hotelId}`,
      icon: <PushpinOutlined />,
    },
    extranetPayment: {
      title: 'Payment',
      link: `/extranet/payment?hotelId=${hotelId}`,
      icon: <CreditCardOutlined />,
    },
  };

  menuItems.contact = {
    title: 'Contact',
    link: `/extranet/contact?hotelId=${hotelId}`,
    icon: <ContactsOutlined />,
  };

  const selectedMenuKey =
    Object.keys(menuItems).find((key) =>
      menuItems[key].link?.includes(pathname)
    ) ?? '';
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [modalOpened, setModalOpened] = useState(false);

  const closeModal = () => {
    setModalOpened(false);
  };

  // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
  const handleOnClick = (hotelId: number) => {
    amplitudeTrack('Click on switch hotels', { hotelId });

    setSearchParams({ hotelId: hotelId.toString() });

    const path = getNewExtranetUrl(hotelId);

    window.history.pushState({ path: path }, '', path);

    setHotelId(hotelId);

    queryClient.invalidateQueries([hotelPath, { hotelId }]);
    closeModal();
  };

  const onItemClick = (item: MenuSection) => {
    if (item.amplitudeEvent) {
      amplitudeTrack('Click menu item', { item: item.title.toLowerCase() });
    }

    window.open(item.link);
  };

  const menuItemsArray: ItemType[] = Object.keys(menuItems).map((key) => {
    const item = menuItems[key];

    return {
      key,
      icon: <span className="subMenuIcon">{item.icon}</span>,
      label: !item.absolute ? (
        <NavLink to={item.link}>
          <div className="subMenuElement">
            {item.title}
            {item.tag && <div className="subTagNew">{item.tag}</div>}
          </div>
        </NavLink>
      ) : (
        <div onClick={() => onItemClick(item)}>
          <div className="subMenuElement">{item.title}</div>
        </div>
      ),
      className: 'standalone-item',
    };
  });

  const hotelName = () => (
    <div className="selector-title">
      {!!hotelInfo && `${hotelInfo.name} `}
      {!!hotelInfo &&
        !!hotelInfo.stars &&
        !!(hotelInfo.stars !== 0) &&
        numberToStars(hotelInfo.stars)}
    </div>
  );

  const getLastNameInitials = (lastName: string) => {
    const lastNameParts = lastName.trim().split(' ');

    return lastNameParts.map((name) => name[0]).join('');
  };

  const handleCopy = (email: string) => {
    copyToClipboard(
      email,
      message.info({
        content: 'Email copied successfully!',
        icon: <InfoCircleOutlined style={{ color: '#808080' }} />,
      })
    );
  };

  return (
    <>
      <Sider className="sider">
        <div>
          <Link to={`/extranet/inventory?hotelId=${hotelId}`}>
            <div className="title">
              <ExtranetLogo />
            </div>
          </Link>
          {hotelsList && hotelsList.length > 1 ? (
            <div onClick={() => setModalOpened(true)} className="selector">
              <div className="selector-div">
                {hotelName()}
                <div>
                  <RightOutlined />
                </div>
              </div>
            </div>
          ) : (
            <div className="selector-hotelier">
              <div className="selector-div">{hotelName()}</div>
            </div>
          )}
          <Menu
            openKeys={openKeys}
            onOpenChange={(elem) => setOpenKeys(elem)}
            theme="dark"
            className="menu"
            mode="inline"
            items={menuItemsArray}
            selectedKeys={[selectedMenuKey]}
          />
        </div>
        {manager && hotelInfo && (
          <div className="bottom_extranet">
            <div className="title-ac">Your account manager</div>
            <div className="info-ac">
              {manager.firstName && manager.lastName && (
                <div className="name-avatar-ac">
                  <PictureComponent
                    className="avatar"
                    style={{ borderRadius: '50%' }}
                    pictureId={manager.avatarId || 'static/default-avatar.png'}
                  />
                  <div className="name-ac">
                    {manager.firstName || `#${manager.id}`}{' '}
                    {getLastNameInitials(manager.lastName)}
                  </div>
                </div>
              )}
              <Tooltip title={manager.email}>
                <div
                  className="email-ac"
                  onClick={() => handleCopy(manager.email)}
                >
                  {manager.email}
                </div>
              </Tooltip>
            </div>
          </div>
        )}
      </Sider>
      <SearchHotelModal
        isOpen={modalOpened}
        onClose={closeModal}
        onSelect={handleOnClick}
      />
    </>
  );
};
